// Inspector for the currently URL-selected node. Reads `nodeKey` from
// the route and looks the node up via the editor context. Edits
// propagate through `editor.updateNode` / `editor.renameNodeKey` so the
// dirty flag and the canvas state stay in sync. The delete button asks
// the shell to clear the panel since the URL would otherwise reference
// a key that no longer exists.
//
// DEV-16196 polish:
//   - The "Node key" field is now actually wired to the node's
//     identifier (not the unused `data.label`), with a draft buffer +
//     commit-on-blur + collision check. Previously typing in the field
//     silently no-op'd.
//   - The "Node type" field is now a styled dropdown (CompactSelect)
//     sourced from /v1/node-types (the runtime registry) instead of a
//     free-text input. Free-text was an MVP placeholder from DEV-16116 —
//     there's a registry now, so we should use it.
import { useClient } from '@cortex/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties } from 'react';

import { CompactSelect } from '../../common/CompactSelect';
import { EmptyState } from '../../common/EmptyState';
import { CompileNodeButton } from '../CompileNodeButton';
import { useWorkflowEditor } from '../WorkflowEditorContext';
import { NodeConfigEditor } from '../node-configs';

import { NodeBindingSection } from './NodeBindingSection';

// Same shape `/v1/node-types` returns — mirrors the runtime registry.
// Duplicates `AddNodeDropdown.INodeTypeMeta` intentionally so each
// consumer can evolve its needs independently (lifting to a shared
// types file is fine when there's a third consumer).
interface INodeTypeMeta {
  key: string;
  description?: string;
  category?: string;
  /** Internal types (e.g. compiled-agent) are compiler output — not hand-authored. */
  internal?: boolean;
}

const lockedTitle = 'Locked — managed by Formant';

const fieldErrorStyle: CSSProperties = {
  color: 'var(--status-danger)',
  fontSize: 'var(--fs-xs)',
};

export function NodeInspectorPanel({
  nodeKey,
  onDeleted,
}: {
  nodeKey: string | null;
  onDeleted: () => void;
}) {
  const editor = useWorkflowEditor();
  const client = useClient();
  const node = editor.getNode(nodeKey);

  // Draft buffer for the nodeKey rename. Holding it locally means we
  // only commit (which rewires every edge) when the user finishes
  // typing — not on every keystroke. Re-seeded whenever the URL pins
  // a different node so switching nodes doesn't carry the prior draft.
  const [draftKey, setDraftKey] = useState<string>(nodeKey ?? '');
  const [keyError, setKeyError] = useState<string | null>(null);
  useEffect(() => {
    setDraftKey(nodeKey ?? '');
    setKeyError(null);
  }, [nodeKey]);

  const nodeTypesQuery = useQuery({
    queryKey: ['node-types'],
    queryFn: () => client.http.get('v1/node-types').json<INodeTypeMeta[]>(),
    staleTime: 60_000,
  });

  if (!nodeKey) {
    return (
      <EmptyState
        title="No node selected"
        description="Click a node on the canvas to inspect or edit its config."
      />
    );
  }
  if (!node) {
    return (
      <EmptyState
        title="Node not found"
        description={`No node with key "${nodeKey}" in the current graph — it may have been deleted.`}
      />
    );
  }

  const data = node.data as {
    label?: string;
    nodeType?: string;
    config?: Record<string, unknown>;
  };
  const currentType = String(data.nodeType ?? '');
  const types = nodeTypesQuery.data ?? [];
  // If the saved type isn't in the registry (legacy workflow, demo
  // data, etc.) we still want it to render as the current selection
  // — fall through to a synthetic option labelled with the raw key.
  const knownType = types.some((t) => t.key === currentType);

  const readOnly = editor.isReadOnly;
  // Managed workflow: this node's key, type and listed config fields belong
  // to the Formant template (the API refuses a change with 409).
  const locked = editor.isNodeLocked(node.id);

  const commitRename = () => {
    if (readOnly || locked) return;
    if (draftKey === node.id) {
      setKeyError(null);
      return;
    }
    const result = editor.renameNodeKey(node.id, draftKey);
    if (result.ok) {
      setKeyError(null);
    } else {
      setKeyError(result.reason);
      setDraftKey(node.id);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
      }}
    >
      {readOnly ? (
        <div
          className="alert"
          style={{
            fontSize: 'var(--fs-xs)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          Read-only — viewing a historical version. Promote it to current to edit.
        </div>
      ) : null}
      {locked ? (
        <div
          className="alert"
          data-testid="node-locked"
          style={{
            fontSize: 'var(--fs-xs)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          {editor.lockedFieldsFor(node.id).has('*')
            ? 'Managed by Formant — this node is locked.'
            : 'Managed by Formant — this node’s structure is locked. Instructions, goal and model stay editable.'}
        </div>
      ) : null}
      <label className="field">
        <span className="field-label">Node key</span>
        <input
          className="input"
          value={draftKey}
          readOnly={readOnly || locked}
          disabled={readOnly || locked}
          title={locked ? lockedTitle : undefined}
          onChange={(e) => {
            setDraftKey(e.target.value);
            if (keyError) setKeyError(null);
          }}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRename();
              (e.target as HTMLInputElement).blur();
            }
          }}
          aria-invalid={Boolean(keyError)}
        />
        <span className="field-help">
          kebab-case, unique within the workflow. Renaming rewires every edge that touches this
          node.
        </span>
        {keyError ? (
          <span style={fieldErrorStyle} role="alert">
            {keyError}
          </span>
        ) : null}
      </label>
      <div className="field">
        <span className="field-label">Node type</span>
        <CompactSelect
          ariaLabel="Node type"
          value={currentType}
          onChange={(next) => editor.updateNode(node.id, { nodeType: next })}
          disabled={nodeTypesQuery.isLoading || readOnly || locked}
          options={[
            ...(!knownType && currentType
              ? [{ value: currentType, label: `${currentType} (unregistered)` }]
              : []),
            // Internal types (compiler output, e.g. compiled-agent) aren't
            // hand-authored — hide them, but keep this node's CURRENT type
            // selectable so an already-compiled node still labels correctly.
            ...types
              .filter((t) => !t.internal || t.key === currentType)
              .map((t) => ({
                value: t.key,
                label: t.category ? `${t.category} · ${t.key}` : t.key,
              })),
          ]}
          width="100%"
        />
        {nodeTypesQuery.error ? (
          <span style={fieldErrorStyle}>
            Failed to load node types: {(nodeTypesQuery.error as Error).message}
          </span>
        ) : null}
      </div>

      {/* Per-node-type config editors live in `../node-configs`. Dispatcher
          looks up the registered form for this node type; unregistered
          types fall back to a raw JSON textarea so they remain editable. */}
      <NodeConfigEditor
        key={node.id}
        nodeType={currentType}
        nodeKey={node.id}
        config={data.config ?? {}}
        onChange={(patch) =>
          editor.updateNode(node.id, { config: { ...(data.config ?? {}), ...patch } })
        }
        onReplace={(c) => editor.updateNode(node.id, { config: c })}
        lockedFields={editor.lockedFieldsFor(node.id)}
      />

      {/* Memory/skill binding only affects `llm-agent` nodes — they're the
          only node type that resolves + injects node-scoped memory/skills at
          run time (resolveMemoriesForNode). Binding to any other node type is a
          runtime no-op, so the section is gated to avoid the footgun (DEV-17006). */}
      {currentType === 'llm-agent' ? (
        <NodeBindingSection
          workflowId={editor.workflow.id}
          nodeKey={node.id}
          readOnly={readOnly}
          persisted={editor.persistedNodeKeys.has(node.id)}
        />
      ) : null}

      {/* Compiling replaces the node with a compiled-agent — a retype, which
          a locked node refuses. */}
      {!readOnly && !locked && currentType === 'llm-agent' ? (
        <CompileNodeButton workflowId={editor.workflow.id} nodeKey={node.id} />
      ) : null}

      {readOnly || locked ? null : (
        <button
          type="button"
          className="btn btn--destructive btn--sm"
          onClick={() => {
            editor.removeNode(node.id);
            onDeleted();
          }}
        >
          Delete node
        </button>
      )}
    </div>
  );
}
